import { useEffect, useRef, useState } from 'react'

/** Milliseconds per character. */
const TYPE_MS = 25
/**
 * How long one ending owns the field, typing included. Holding the period
 * rather than the still time is what keeps the rhythm even: a long ending
 * takes longer to type and rests for correspondingly less.
 */
const PERIOD_MS = 2400
/** Floor on the still time, so a long ending is still readable once typed. */
const MIN_HOLD_MS = 900

/**
 * Types a field's placeholder: a stem that stays put, and an ending that
 * rotates through the ways in.
 *
 * Only the ending is typed. Holding the stem still means the field always
 * states what it does, and the movement is confined to the half that is
 * actually saying something new.
 *
 * A finished ending is not deleted: it gives way to the next one starting
 * from its first character, which keeps the eye on what is arriving rather
 * than on what is leaving. The first ending types itself like every other
 * one, so landing on the page starts the rhythm rather than interrupting it.
 *
 * Focus does not stop it. A caret in an empty field is someone deciding what
 * to write, which is exactly when the routes are worth reading; `paused` is
 * for the moment they start writing.
 *
 * The field needs its own `aria-label`. A placeholder that rewrites itself on
 * a timer is not a usable accessible name.
 */
export function useTypedPlaceholder(
  stem: string,
  endings: readonly string[],
  paused: boolean,
) {
  const [shown, setShown] = useState(stem)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (paused || endings.length === 0) return () => {}

    const reduced =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    let line = 0
    let chars = 0
    let cancelled = false

    const step = () => {
      if (cancelled) return
      const target = endings[line]

      // Reduced motion gets the rotation without the typing.
      if (!reduced && chars < target.length) {
        chars += 1
        setShown(stem + target.slice(0, chars))
        timer.current = window.setTimeout(step, TYPE_MS)
        return
      }
      if (reduced) setShown(stem + target)

      const hold = Math.max(PERIOD_MS - target.length * TYPE_MS, MIN_HOLD_MS)
      timer.current = window.setTimeout(() => {
        if (cancelled) return
        line = (line + 1) % endings.length
        chars = 0
        step()
      }, hold)
    }

    step()

    return () => {
      cancelled = true
      if (timer.current !== undefined) window.clearTimeout(timer.current)
    }
  }, [paused, stem, endings])

  return shown
}
